import { supabase, invokeFunction } from '@/lib/supabase';
import type { Child, ChildCreateInput, ChildUpdateInput } from '@/types';

export interface SchoolResult {
  name: string;
  address: string;
  placeId: string;
}

/** Min length before we hit Google Places — 3 filters out prefix-noise like "me", "sa". */
export const SCHOOL_QUERY_MIN_LENGTH = 3;

// Tiny in-memory cache so backspace-retype ("merid" → "meri" → "merid") doesn't
// re-hit Places. Module-scoped → shared across AddEditChildForm instances in
// the same tab. Keyed by normalized query; 5-minute TTL trades freshness for
// cost (Places results don't change minute-to-minute for school autocomplete).
const SCHOOL_CACHE_TTL_MS = 5 * 60 * 1000;
const schoolCache = new Map<
  string,
  { results: SchoolResult[]; expiresAt: number }
>();

export async function searchSchools(query: string): Promise<SchoolResult[]> {
  const normalized = query?.trim().toLowerCase() ?? '';
  if (normalized.length < SCHOOL_QUERY_MIN_LENGTH) return [];

  const cached = schoolCache.get(normalized);
  if (cached && cached.expiresAt > Date.now()) return cached.results;

  const { data, error } = await invokeFunction('search-schools', {
    body: { query: query.trim() },
  });
  if (error) {
    console.warn('[searchSchools] error:', error);
    return [];
  }
  const results: SchoolResult[] = data?.results ?? [];
  schoolCache.set(normalized, {
    results,
    expiresAt: Date.now() + SCHOOL_CACHE_TTL_MS,
  });
  return results;
}

export async function createChild(
  householdId: string,
  input: ChildCreateInput,
): Promise<Child> {
  const { data, error } = await supabase
    .from('children')
    .insert({ ...input, household_id: householdId })
    .select()
    .single();

  if (error) throw error;
  return data!;
}

export async function updateChild(
  childId: string,
  updates: ChildUpdateInput,
): Promise<Child> {
  const { data, error } = await supabase
    .from('children')
    .update(updates)
    .eq('id', childId)
    .select()
    .single();

  if (error) throw error;
  return data!;
}

export async function deleteChild(childId: string): Promise<void> {
  const { error } = await supabase.from('children').delete().eq('id', childId);

  if (error) throw error;
}
